/**
 * Integrations-marketplace slice of the English catalog.
 *
 * en.ts sits close to the 500-line fileSize ceiling and is a documented merge
 * hotspot, so integration copy lives here and is spread back into `en` in
 * en.ts — t() and MessageKey unchanged. Same carve-out pattern as enFunnels /
 * enAgent / enBoards. WhatsApp templates + conversation starters live in
 * ./enMessagingAssets and are spread in here (en.ts has no room for an import).
 */
import { enMessagingAssets } from './enMessagingAssets';
import { enImport } from './enImport';
import { enGmail } from './enGmail';
import { enTikTokAds } from './enTikTokAds';
import { enDisconnect } from './enDisconnect';
import { enWhatsAppCoexistence } from './enWhatsAppCoexistence';

export const enIntegrations = {
  ...enMessagingAssets,
  ...enImport,
  ...enGmail,
  ...enTikTokAds,
  ...enDisconnect,
  ...enWhatsAppCoexistence,
  // Shared IntegrationModal chrome (non-OAuth setup sheets).
  'integrations.modalClose': 'Close',
  // ── Website snippet card ──────────────────────────────────────────────
  'snippet.intro':
    'Paste this once, just before the closing </body> tag on every page of your site. It captures form submissions as leads, tracks which products people view and add to the cart, and shows your popups — one tag, no server code, no other setup.',
  'snippet.embedTag': 'Embed tag',
  'snippet.publishableKey': 'Publishable key',
  'snippet.keyHint': 'Safe to expose in browser code — it only works from the websites you list below.',
  'snippet.allowedWebsites': 'Allowed websites',
  // This string once claimed an empty list "accepts nothing, which is the safe
  // default" while isOriginAllowed() returned true for an empty list — the
  // copy described the safe behaviour and the code did the opposite. Rather
  // than keep describing a fail-open control accurately, the control was made
  // fail-closed (see isOriginAllowed). The copy and the code now agree, and
  // what they agree on is the safe thing.
  'snippet.allowedHint':
    'Comma separated — you must fill this in before the tag will capture anything. Requests from any other website are refused.',
  'snippet.save': 'Save websites',
  'snippet.saving': 'Saving…',

  // ── Social channel manage screen (YouTube, X, LinkedIn) ─────────────────────────
  // One screen serves both, so the only per-channel copy is these two
  // subtitles and what an account is CALLED there — a YouTube connection is a
  // channel, an X connection is an account, and saying "account" on YouTube
  // reads as the Google login rather than the thing posts go to.
  'socialChannel.youtubeSubtitle': 'Upload videos to your channel from the content calendar',
  'socialChannel.xSubtitle': 'Publish posts and threads to your X account',
  'socialChannel.linkedinSubtitle': 'Publish posts, photos and videos to your LinkedIn profile',
  'socialChannel.channelNoun': 'Channel',
  'socialChannel.channelId': 'Channel ID: {id}',
  'socialChannel.accountNoun': 'Account',
  'socialChannel.accountId': 'Account ID: {id}',
  'socialChannel.profileNoun': 'Profile',
  'socialChannel.checkConnection': 'Check connection',
  'socialChannel.checking': 'Checking…',
  'socialChannel.healthFailed': 'Could not run the connection check. Try again.',
  'socialChannel.health.ok': 'Working',
  'socialChannel.health.reconnect': 'Needs reconnecting',
  'socialChannel.health.error': 'Could not verify',
  'socialChannel.backToIntegrations': 'Back to Integrations',
  'socialChannel.connectionStatus': 'Connection status',
  'socialChannel.notConnected': 'Not connected',
  'socialChannel.connectAccount': 'Connect account',
  'socialChannel.connectAnother': 'Connect another',
  'socialChannel.redirecting': 'Redirecting…',
  'socialChannel.redirectNote': "You'll be redirected to authorize access",
  'socialChannel.disconnect': 'Disconnect',
  'socialChannel.disconnecting': 'Disconnecting…',
  'socialChannel.disconnected': 'Disconnected.',
  'socialChannel.disconnectFailed': "Couldn't disconnect. Try again.",
  'socialChannel.connectFailed': 'Failed to connect. Check the credentials in settings.',
  // Deliberately NOT "not connected": an unread status and an absent
  // connection are different facts, and only one of them is the merchant's
  // problem to fix.
  'socialChannel.loadFailed': "Couldn't check this connection. Reload to try again.",

  // ── Category pills (Apps / Custom API / Custom MCP / Projects) ────────
  'integrations.category.apps': 'Apps',
  'integrations.category.customApi': 'Custom API',
  'integrations.category.customMcp': 'Custom MCP',
  'integrations.category.projects': 'Projects',

  // ── Row states beyond connected / not connected ───────────────────────
  // Amber, not green and not blank: the connection exists and is fine, it is
  // simply waiting on the first customer action — which no reconnect hurries.
  'integrations.awaitingFirstLead': 'Waiting for first lead',
  // Grey badge: the server is not configured for this provider yet, so there
  // is nothing to connect to. Shown in place of the Connect button.
  'integrations.comingSoon': 'Coming soon',
  'integrations.statusChecking': 'Checking…',
  'integrations.statusFailed': 'Couldn’t check the status',
  'integrations.statusRetry': 'Retry',

  // ── View Profile modal (provider-specific fields; real APIs only) ─────
  'integrations.profile.loading': 'Loading profile',
  'integrations.profile.loadFailed': 'Could not load account details. Try again later.',
  'integrations.profile.noDetails': 'No account details available for this connection.',
  'integrations.profile.fallbackDescription': 'Connect this tool to BuzzBit X',
  'integrations.profile.status': 'Status',
  'integrations.profile.accountName': 'Account',
  'integrations.profile.platformId': 'Account ID',
  'integrations.profile.connectedAt': 'Connected',
  'integrations.profile.tokenExpires': 'Token expires',
  'integrations.profile.pageName': 'Page',
  'integrations.profile.pageId': 'Page ID',
  'integrations.profile.username': 'Username',
  'integrations.profile.accountId': 'Account ID',
  'integrations.profile.storeName': 'Store',
  'integrations.profile.shopDomain': 'Shop domain',
  'integrations.profile.email': 'Email',
  'integrations.profile.currency': 'Currency',
  'integrations.profile.timezone': 'Timezone',
  'integrations.profile.lastSync': 'Last sync',
  'integrations.profile.syncCounts': 'Synced data',
  'integrations.profile.syncCountsValue':
    '{products} products · {orders} orders · {customers} customers',
  'integrations.profile.adAccount': 'Ad account',
  'integrations.profile.adAccountId': 'Ad account ID',
  'integrations.profile.otherAdAccount': 'Other ad account',
  'integrations.profile.keyCount': 'API keys',
  'integrations.profile.domain': 'Domain',
  'integrations.profile.senderName': 'Sender name',
  'integrations.profile.replyTo': 'Reply-to',
  'integrations.profile.instanceUrl': 'Instance URL',
  'integrations.profile.provider': 'Provider',
  'integrations.profile.endpoint': 'Endpoint',
  'integrations.profile.eventCount': 'Events received',
  'integrations.profile.lastEvent': 'Last event',
  'integrations.profile.webhookEndpoints': 'Endpoints',
  'integrations.profile.webhookActive': 'Active endpoints',
  'integrations.profile.webhookEvents': 'Subscribed events',
  'integrations.profile.paymentCount': 'Payments',
  'integrations.profile.lastPayment': 'Last payment',
  'integrations.profile.channel': 'Channel',
  'integrations.profile.selected': 'Selected',
  'integrations.profile.connectedAccounts': 'Connected accounts',
  'integrations.profile.accountStatus': 'Account status',
  'integrations.profile.followers': 'Followers',
  'integrations.profile.following': 'Following',
  'integrations.profile.subscribers': 'Subscribers',
  /** Shown when the provider/scopes cannot return a count (never a mock number). */
  'integrations.profile.metricUnavailable': 'Not available',

  // ── Page toasts (OAuth callbacks land here) ───────────────────────────
  'integrations.toast.metaMismatch':
    'This workspace is already connected to a different Meta account. Disconnect Meta first, then connect the new account.',
  'integrations.toast.connected': '{name} connected successfully!',
  'integrations.toast.metaFailed': 'Meta connection failed. Please try again.',
  'integrations.toast.metaNoPages':
    'Facebook granted no Page, so nothing was connected. The login configuration must include Pages and Instagram accounts, and the Page must be selected in the dialog.',
  'integrations.toast.gmailConnected': 'Gmail AI Support connected successfully!',
  'integrations.toast.gmailFailed': 'Gmail could not connect. Click Connect and try again.',
  'integrations.toast.shopifyConnected':
    'Shopify connected successfully! Syncing products, customers & orders...',
  // The inline banner that stays after the toast is gone: the return from
  // Shopify lands here with the page still loading its tiles, and a toast
  // that fired during that second was missed on every recorded run.
  'integrations.banner.shopifyConnected.title': 'Shopify is connected',
  'integrations.banner.shopifyConnected.body':
    'Your products, customers and orders are syncing now — most stores finish within a few seconds.',
  'integrations.banner.shopifyConnected.dismiss': 'Dismiss',
  // The onboarding connect step names the store it found attached, so a
  // merchant arriving from the App Store install sees WHICH store is connected.
  'onboarding.connect.connectedStore': '{store} — Connected',
  'integrations.toast.shopifyFailed': 'Shopify connection failed',
  'integrations.toast.shopifyFailedReason': 'Shopify connection failed: {reason}',
  // One sentence per `reason` the server sends back from the Shopify return leg.
  'integrations.toast.shopifyReason.intentExpired':
    'The Shopify connect link expired or was opened in another browser. Click Install again and finish within 15 minutes.',
  'integrations.toast.shopifyReason.storeOwnedByAnotherWorkspace':
    'This Shopify store is already connected to another BuzzBit X workspace. Disconnect it there first, or ask that workspace owner to invite you.',
  'integrations.toast.shopifyReason.workspaceHasStore':
    'This workspace already has a different Shopify store connected. Disconnect it before connecting another one.',
  'integrations.toast.shopifyReason.missingScopes':
    'Shopify granted fewer permissions than BuzzBit X needs. Click Install again and approve every permission on the Shopify screen.',
  'integrations.toast.shopifyReason.appIdentityDrift':
    'The install came from a different Shopify app than this BuzzBit X is configured for. Reinstall from the Shopify App Store listing.',
  'integrations.toast.shopifyReason.cacheUnavailable':
    'BuzzBit X could not save the connection state. Please try again in a moment.',
  'integrations.toast.shopifyReason.finalizeFailed':
    'The store was installed on Shopify but could not be attached to this workspace. Click Install again to retry.',
  // The in-app "Connect Shopify" modal: store-specific OAuth via /install-auth.
  'integrations.shopifyConnect.title': 'Connect Shopify',
  'integrations.shopifyConnect.subtitle': 'Authorize your Shopify store',
  'integrations.shopifyConnect.body':
    "Enter your store's myshopify domain. You'll authorize BuzzBit X on Shopify — we'll save your store details automatically. No access tokens to copy.",
  'integrations.shopifyConnect.shopLabel': 'Shopify store domain',
  'integrations.shopifyConnect.shopPlaceholder': 'your-store.myshopify.com',
  'integrations.shopifyConnect.install': 'Connect Shopify',
  'integrations.shopifyConnect.listingPending':
    'Shopify connect is not available right now. Please try again shortly, or contact support if this continues.',
  'integrations.shopifyConnect.invalidShop':
    'Enter a valid store domain like your-store.myshopify.com (or just your-store).',
  'integrations.shopifyConnect.starting': 'Opening Shopify…',
  'integrations.shopifyConnect.startFailed': "Couldn't start the Shopify connection. Check the store name and try again.",
  'integrations.shopifyConnect.close': 'Close',
  // Signup-page notices for the Shopify App Store install handoff.
  'integrations.handoff.yourStore': 'your Shopify store',
  'integrations.handoff.signedInAs': 'You are signed in as {email}.',
  'integrations.handoff.connectHereQuestion': 'Connect {store} to this account?',
  'integrations.handoff.connectHere': 'Connect to this account',
  'integrations.handoff.connecting': 'Connecting…',
  'integrations.handoff.useDifferentAccount': 'Use a different account',
  'integrations.handoff.connected': 'Almost done — create your BuzzBit account to finish connecting {store}.',
  'integrations.handoff.pending':
    'The Shopify install for {store} expired before it could be attached. Create your account, then open BuzzBit X again from your Shopify admin to reconnect.',
  'integrations.handoff.error':
    'Shopify connection failed. Create your account and reinstall the app from your Shopify admin, or connect the store from Settings → Integrations.',
  'integrations.handoff.errorReason':
    'Shopify connection failed ({reason}). Create your account and reinstall the app from your Shopify admin, or connect the store from Settings → Integrations.',
  // Sign-in page: the return from Shopify found no session (a reinstall from
  // the Shopify admin, another browser). The store is already connected; the
  // merchant only has to sign in to reach it.
  'integrations.handoff.reconnectSignIn': 'Sign in to finish reconnecting {store}.',
  // ── Shopify settings page: Data Sync card and disconnect warning ─────
  // Counts come from GET /integrations/shopify/sync-status. A re-sync is a
  // queued job, so the card watches `lastSyncAt` move rather than guessing.
  'shopifySettings.sync.title': 'Data Sync',
  'shopifySettings.sync.products': 'Products',
  'shopifySettings.sync.customers': 'Customers',
  'shopifySettings.sync.orders': 'Orders',
  'shopifySettings.sync.lastSynced': 'Last synced {when}',
  'shopifySettings.sync.neverSynced': 'Not synced yet',
  'shopifySettings.sync.loadFailed': "Couldn't load the synced counts. Reload the page to try again.",
  'shopifySettings.sync.resync': 'Re-sync All Data',
  'shopifySettings.sync.syncing': 'Syncing...',
  'shopifySettings.sync.started': 'Sync started — the numbers above update as your data arrives.',
  'shopifySettings.sync.finished': 'Sync finished — the numbers above are up to date.',
  'shopifySettings.sync.stillRunning':
    'Still syncing — large stores can take a few minutes. Reload this page later to see the final numbers.',
  'shopifySettings.sync.failed': 'Sync failed. Please try again.',
  'shopifySettings.sync.toastStarted': 'Sync started',
  'shopifySettings.sync.toastFailed': 'Sync failed',
  // Disconnect deletes the connection and archives this store's products
  // (server baseIntegrationOps → retireShopifyProductsOnDisconnect); a
  // reconnect's sync restores them. Customers and orders are not touched.
  'shopifySettings.disconnect.warning':
    "Disconnecting stops syncing and removes BuzzBit X from your Shopify admin. Your customers and orders stay in BuzzBit X; this store's products are archived until you reconnect.",
  'integrations.toast.connectionFailed': 'Connection failed: {reason}',
  'integrations.toast.nothingToConfigure': 'Connected — there is nothing else to configure for this one.',
  'integrations.toast.connectFailed': 'Failed to connect. Check credentials in settings.',
  'integrations.metaLabel': 'Meta (Messenger + Instagram)',

  // ── Self-managed tiles (the ones outside the registry) ────────────────
  'tile.websiteSnippet.name': 'Website snippet (no code)',
  'tile.websiteSnippet.description':
    'Paste one tag on your site — form leads, product views, cart activity and popups. No server needed.',
  'tile.landingPage.name': 'Website & landing page',
  'tile.landingPage.description': 'Turn form submissions on your page into leads and automation triggers.',
  'tile.invoicing.name': 'Invoicing & receipts',
  'tile.invoicing.description': 'Connect Morning or Invoice4U — every payment issues a tax receipt automatically.',
  'tile.apiConnections.name': 'API connections',
  'tile.apiConnections.description':
    'API keys and OAuth2 connections. Workflows reference them by name — secrets stay encrypted and never reach the browser.',
  'tile.apiConnections.add': 'Add',
  'tile.apiConnections.save': 'Save connection',
  'tile.apiConnections.encryptedHint':
    'Stored encrypted (AES-256-GCM). The key is never shown again after saving.',
  'tile.apiConnections.oauthBadge': 'OAuth2',
  'tile.cardcom.name': 'Collect payments · Cardcom',
  'tile.cardcom.description': 'Send payment links from your own Cardcom terminal — who paid what, on every customer.',
  // V54 — webhook-key connections, configurable in-app on every plan.
  'tile.stripe.name': 'Stripe',
  'tile.stripe.description': 'Trials, subscriptions, cancellations and failed payments from your Stripe account — MRR on your Hub and SaaS flows that fire.',
  'tile.coursePlatform.name': 'Course platform',
  'tile.coursePlatform.description': 'Enrollments and completions from Teachable, Kajabi, Thinkific or a Make/Zapier webhook.',
  'tile.calendly.name': 'Calendly',
  'tile.calendly.description': 'Bookings made in Calendly land on the customer and start booking flows.',
  'tile.calcom.name': 'Cal.com',
  'tile.calcom.description': 'Bookings made in Cal.com land on the customer and start booking flows and reminders.',
  'webhookKeys.howTo.stripe': 'In Stripe → Developers → Webhooks, add an endpoint with this URL and the events below, then paste its signing secret (whsec_…) here.',
  'webhookKeys.howTo.course': 'Point your course platform (or a Make/Zapier step) at this URL, signing each request with the secret. Paste the secret your platform gave you, or let us make one.',
  'webhookKeys.howTo.calendly': 'Create a Calendly webhook subscription to this URL for the events below, then paste its signing key here.',
  'webhookKeys.howTo.calcom': 'In Cal.com → Settings → Developer → Webhooks, add this URL for the events below, then paste the webhook secret here.',
  'webhookKeys.secretLabel.stripe': 'Signing secret (whsec_…)',
  'webhookKeys.secretLabel.course': 'Signing secret',
  'webhookKeys.secretLabel.calendly': 'Webhook signing key',
  'webhookKeys.secretLabel.calcom': 'Webhook secret',
  'webhookKeys.webhookUrl': 'Webhook URL',
  'webhookKeys.events': 'Events to enable',
  'webhookKeys.save': 'Save',
  'webhookKeys.generate': 'Make a secret for me',
  'webhookKeys.generatedLabel': 'Your signing secret',
  'webhookKeys.generatedOnce': 'Copy it into your course platform now — it is shown only once. Saving a new one replaces it.',
  'webhookKeys.encrypted': 'Stored encrypted. Events are checked against it before anything runs.',
  'webhookKeys.secretRequired': 'Paste the signing secret from the platform, then save.',
  'webhookKeys.saved': '{name} connected — events will start arriving.',
  'webhookKeys.saveFailed': 'Could not save. Check the secret and try again.',
  'webhookKeys.connected': 'Connected',
  'webhookKeys.needsReconnect': 'Secret no longer readable — paste it again to keep receiving events.',
  'webhookKeys.lastEvent': 'Last event received {when}',
  'webhookKeys.waitingFirstEvent': 'Waiting for the first event. Send a test event from {name} to check the connection.',
  'webhookKeys.copy': 'Copy',
  'webhookKeys.copied': 'Copied',

  // ── Website snippet card ──────────────────────────────────────────────
  'snippet.getSnippet': 'Get snippet',
  'snippet.loading': 'Loading…',
  'snippet.loadFailed': 'Could not load your website key. Try again.',
  'snippet.saved': 'Allowed websites updated.',
  'snippet.saveFailed': 'Could not save those domains.',
  'snippet.modalTitle': 'Website snippet',
  'snippet.modalSubtitle': 'One tag on your site: form leads, product views, cart activity and popups.',

  // ── Landing page card ─────────────────────────────────────────────────
  'landing.connectFailed': 'Could not connect. Try again.',
  'landing.statusFailed': 'Could not check the connection status. Try again.',
  'landing.receiving': 'Receiving leads',
  'landing.waiting': 'Waiting for first lead',
  'landing.verifyOk': 'Connected — receiving leads from your page.',
  'landing.verifyNone': 'No lead received yet. Send a test form submission, then verify again.',

  // ── Meta card actions ─────────────────────────────────────────────────
  'meta.labelMeta': 'Facebook & Instagram',
  'meta.labelWhatsapp': 'WhatsApp',
  'meta.since': 'since {date}',
  'meta.toast.sdkUnavailable':
    'The Facebook login window could not load. Allow connect.facebook.net (ad blockers block it) and try again.',
  'meta.toast.popupCancelled': 'Facebook login was cancelled before a Page was granted.',
  'meta.toast.popupFallbackRedirect': 'The Facebook window did not finish — opening the Facebook login page instead.',
  'meta.toast.noAuthUrl': 'Could not start the connection — no auth URL returned.',
  'meta.toast.connectFailed': 'Could not start the connection — please try again.',
  'meta.toast.testOk': 'Connection is working.',
  'meta.toast.testFailed': 'Connection test failed — the token may have expired.',
  'meta.toast.testFailedReconnect':
    'Connection test failed — the token may have expired. Reconnect to refresh it.',
  'meta.disconnectConfirmTitle': 'Disconnect {label}?',
  'meta.disconnectConfirm': 'This stops all automated messages on {label}.',
  'meta.toast.disconnected': '{label} disconnected',
  'meta.toast.disconnectFailed': 'Failed to disconnect {label}',
  'meta.whatsappNeedsSetup': 'WhatsApp number not linked',
  'meta.whatsappFinishSetup': 'Finish setup',
  'meta.whatsappActivationIncomplete': 'WhatsApp number linked but not activated — webhooks or registration failed',

  'meta.waSignup.sdkBlocked':
    "Couldn't reach Facebook's login script. An ad blocker or privacy extension usually causes this — try again in a private window with extensions off.",
  'meta.waSetup.title': 'Link your WhatsApp number',
  'meta.waSetup.intro':
    'Your WhatsApp is authorised but no number is linked yet, so nothing can arrive. Pick the number customers write to.',
  'meta.waSetup.chooseAccount': 'WhatsApp Business Account',
  'meta.waSetup.chooseNumber': 'Number',
  'meta.waSetup.use': 'Use this',
  'meta.waSetup.linked': 'Linked',
  'meta.waSetup.refresh': 'Refresh',
  'meta.waSetup.noAccounts':
    'No WhatsApp Business Account is visible to this connection. Reconnect and choose the Business that owns your number.',
  'meta.waSetup.noNumbers': 'This account has no phone numbers yet. Add one in WhatsApp Manager, then refresh.',
  'meta.waSetup.accountsFailed': "Couldn't load your WhatsApp accounts. Try again in a minute, or reconnect WhatsApp.",
  'meta.waSetup.numbersFailed': "Couldn't load the numbers on this account. Try refreshing.",
  'meta.waSetup.numberTaken': 'Another workspace already receives on this number.',
  'meta.waSetup.connectFailed': "Couldn't link that number. Try again in a minute, or reconnect WhatsApp.",

  // ── Meta webhook delivery check ───────────────────────────────────────
  // "Test" proves the token; THIS proves Meta will deliver. They fail
  // independently, and "connected but nothing comes through" is the second.

  // ── Meta connection doctor ────────────────────────────────────────────
  // The 2026-09 outage: three Meta apps under one name (webhook subscription
  // on one, server secret from another, token from a third) and every check
  // needed the Meta dashboard. This is that dashboard trip, inside the product.
  'meta.doctor.run': 'Diagnose connection',
  'meta.doctor.fix': 'Fix webhooks',
  'meta.doctor.title': 'Connection doctor',
  'meta.doctor.intro':
    'Each row is one fact Meta or this server reported. Red rows stop inbound entirely; fix them top to bottom. "Fix webhooks" re-points the app subscriptions at this server and re-subscribes your Page.',
  'meta.doctor.failed': "Couldn't run the diagnosis. Try again.",
  'meta.doctor.redirectUris': 'Valid OAuth Redirect URIs — Meta must have these exactly:',
  'meta.doctor.redirectUrisHint':
    'Paste both into your Meta app under Facebook Login \u2192 Settings, and turn on Client OAuth Login and Web OAuth Login. A connect that ends on Meta\u2019s "URL Blocked" page is this list not matching \u2014 Meta\u2019s error names no address, so compare it to these character for character.',
  'meta.doctor.serverApp': 'Server app',
  'meta.doctor.clientApp': 'This app',
  'meta.doctor.tokenApp': 'Token issued by',
  'meta.doctor.lastWebhook': 'Last delivery',
  'meta.doctor.notSet': 'not set',
  'meta.doctor.unknown': 'unknown',
  'meta.doctor.notConnected': 'not connected',
  'meta.doctor.lastWebhookNever': 'Meta has never called this server.',
  'meta.doctor.lastWebhookAccepted': '{when} · {platform} · accepted',
  'meta.doctor.lastWebhookRejected': '{when} · refused: {reason}',
  'meta.doctor.clientAppMismatch':
    'This site sends you to authorise Meta app {client}, but the server is app {server}. Deliveries will be signed by one app and verified with the other. The two deployments must name the same app.',
  'meta.doctor.reconnectHint': 'The stored token does not belong to this app. Reconnect Meta to issue a new one.',
  'meta.doctor.reconnect': 'Reconnect',
  // The connected Meta card's own Reconnect (modal button row), not the doctor's.
  'meta.reconnect': 'Reconnect',
  'meta.doctor.stepOk': 'done',
  'meta.doctor.stepFailed': 'failed',

  // ── AI host setup modal ───────────────────────────────────────────────
  'ai.setup.subtitle': 'Your AI signs in from its side — nothing is stored here.',
  'ai.setup.loadFailed': "Couldn't load the setup steps. Reload to try again.",
  'ai.setup.pickHost': 'Which app runs your model?',
  'ai.setup.first': 'First, once',
  'ai.setup.where': 'Where it goes',
  'ai.setup.paste': 'Paste this',
  'ai.setup.run': 'Run this',
  'ai.setup.copy': 'Copy',
  'ai.setup.copied': 'Copied',
  'ai.setup.bulkSends': 'Bulk sends',
  'ai.setup.headless': 'Headless or CI',
  'ai.setup.ollama': 'Using Ollama?',
  'ai.setup.keys': 'Manage MCP keys',
  'ai.setup.docs': 'Full guide',

  // ── Instagram tile (Integrations page) ────────────────────────────────
  // Instagram can connect on its own (platform=instagram) or ride an existing
  // Facebook grant. Disconnect here removes Instagram only.
  'meta.igTitle': 'Instagram',
  'meta.igSubtitle':
    'Connect your Instagram Professional account for DMs, story replies and comments in the inbox.',
  'meta.igPickAction': 'Choose account',
  'meta.igNoneChosen': 'No Instagram account chosen',
  'meta.igConnectFirst':
    'Connect Instagram to choose which Professional account this workspace uses for DMs and comments.',
  'meta.igPickHint':
    'Only a Page with an Instagram business account linked to it can be chosen. If yours is missing, link it in Meta Business Suite, then reload this list.',
  'meta.igHandle': 'Account',
  'meta.advancedDiagnostics': 'Advanced: delivery and app diagnostics',
  'meta.igManageTitle': 'Instagram account',
  'meta.igReconnectHint':
    'Reconnect renews Instagram permissions for this workspace. Delete removes only the Instagram connection — Facebook stays connected.',
  'meta.ig.connect': 'Connect Instagram',
  'meta.ig.reconnect': 'Reconnect',
  'meta.ig.sync': 'Sync Account',
  'meta.ig.delete': 'Delete',
  'meta.ig.deleteConfirm':
    'Delete the Instagram connection for this workspace? Facebook stays connected.',
  'meta.ig.deleteConfirmYes': 'Yes, delete Instagram',
  'meta.ig.deleteCancel': 'Cancel',
  'meta.ig.syncOk': 'Instagram account synced.',
  'meta.ig.syncFailed': 'Could not sync Instagram. Try again in a minute.',
  'meta.ig.syncNeedsReconnect': 'Instagram needs reconnect before sync can finish.',
  'meta.ig.selected': 'Instagram account selected.',
  'meta.ig.selectFailed': 'Could not switch the Instagram account.',
  'meta.ig.accountsTitle': 'Instagram accounts',
  'meta.ig.accountsLoadFailed': 'Could not load Instagram accounts from Meta.',
  'meta.ig.accountsEmpty': 'No Instagram accounts on this connection.',
  'meta.ig.accountsEmptyHint':
    'Link an Instagram Professional account to a Facebook Page in Meta Business Suite, then reconnect or sync.',
  'meta.ig.searchPlaceholder': 'Search accounts…',
  'meta.ig.retry': 'Retry',
  'meta.ig.footer': 'Showing all {count} Instagram accounts returned by Meta.',
  'meta.ig.footerCapped':
    'Showing {count} Instagram accounts (list truncated at the safety cap — contact support if one is missing).',
  'meta.ig.needsReauth':
    'This Instagram connection needs re-authentication — its token expired or was revoked.',
  'meta.ig.tokenExpiring':
    'The Instagram access token expires in {days} day(s). Reconnect if renewal fails.',
  'meta.ig.lastSync': 'Last sync {date}',
  'meta.ig.linkedPageHint': 'Linked Page: {page}',
  'meta.ig.featuresTitle': 'Features & permissions',
  'meta.ig.featuresHint':
    'Profile and messaging are required. Publish, comments and insights need Meta App Review.',
  'meta.igFeature.profile': 'Profile & basic access',
  'meta.igFeature.messaging': 'Direct messages',
  'meta.igFeature.publish': 'Publish content',
  'meta.igFeature.comments': 'Manage comments',
  'meta.igFeature.insights': 'Insights',
  'meta.igFeature.available': 'Available',
  'meta.igFeature.missing': 'Missing — reconnect',
  'meta.igFeature.declined': 'Declined',
  'meta.igFeature.needsReview': 'Needs App Review',
  'meta.igFeature.unknownBase': 'Unknown — sync or reconnect',
  'meta.igAccountType': 'Account type',
  'meta.igType.business': 'Business',
  'meta.igType.creator': 'Creator',

  // ── Meta Ads connection (Integrations page) ───────────────────────────
  // A separate grant from the messaging connect: different scopes, its own
  // credential, its own ad-account picker. The messaging token carries no ads
  // scope, so campaign figures need this connection and nothing else.
  'adsConnect.subtitle':
    'Connect the ad account this workspace reports on. Separate from the Meta messaging connection — it asks for ads permissions.',
  'adsConnect.checking': 'Checking the connection…',
  'adsConnect.noAccounts': 'No ad account visible',
  'adsConnect.noAccountsBody':
    'The connection worked, but Meta returned no ad account for this login. Grant this Meta user access to the ad account in Business Manager, then refresh the list.',
  'adsConnect.pickPrompt': 'Pick the ad account whose campaigns this workspace reads.',
  'adsConnect.inUse': 'In use',
  'adsConnect.refresh': 'Refresh list',
  'adsConnect.disconnect': 'Disconnect',
  'adsConnect.disconnectConfirmTitle': 'Disconnect Meta Ads?',
  'adsConnect.disconnectConfirm':
    'Campaign spend and results stop updating until you connect again.',
  'adsConnect.disconnected': 'Meta Ads disconnected.',
  'adsConnect.reconnect': 'Reconnect',
  'adsConnect.needsReauth': 'Needs re-authentication',
  'adsConnect.readOnly':
    'Read-only access. Campaign figures sync; nothing in your ad account can be changed from here.',
  'adsConnect.canManage':
    'Full access. The assistant can draft campaigns, and every draft stays paused until you activate it.',
  'adsConnect.connectFailed': 'Could not start the Meta Ads connection.',
  'adsConnect.loadFailed': 'Could not load the ad accounts.',
  'adsConnect.selectFailed': 'Could not switch the ad account.',
  'adsConnect.refreshFailed': 'Could not reach Meta for the account list.',
  'adsConnect.disconnectFailed': 'Could not disconnect Meta Ads.',
  'adsConnect.accountSwitched': 'Ad account switched.',
  // ── Capability matrix (integrations prove themselves) ────────────────
  'integrations.verify.title': 'What each connection can do right now',
  'integrations.verify.subtitle':
    'Every connection is checked with one real call per capability, right after it is made and whenever you ask. A green badge above means the check passed — not just that a connection exists.',
  'integrations.verify.run': 'Verify now',
  'integrations.verify.running': 'Verifying…',
  'integrations.verify.never': 'Nothing has been verified yet. Connect a channel, or verify now.',
  'integrations.verify.checkedAt': 'Last checked {when}',
  'integrations.verify.notConnected': 'Not connected',
  'integrations.verify.notConnectedList': 'Not connected yet: {platforms}',
  'integrations.verify.failed': 'Could not run the checks. Try again in a moment.',
  'integrations.verify.done': 'Checks finished: {ok} of {total} passed.',
  'integrations.verify.status.ok': 'Works',
  'integrations.verify.status.scope_missing': 'Permission missing — reconnect and grant it',
  'integrations.verify.status.app_review_pending': 'Waiting on the platform\'s app review',
  'integrations.verify.status.tier_required': 'Needs a paid API tier or credits on the platform',
  'integrations.verify.status.dev_token_missing': 'Developer token not configured',
  'integrations.verify.status.project_not_approved': 'Google Cloud project not approved for the Ads API — apply for Basic access',
  'integrations.verify.status.token_expired': 'Access expired — reconnect',
  'integrations.verify.status.rate_limited': 'Rate limited — checked again later',
  'integrations.verify.status.error': 'Check failed',
  'integrations.verify.capability.auth': 'Sign-in',
  'integrations.verify.capability.webhooks': 'Incoming events',
  'integrations.verify.capability.messaging': 'Messages',
  'integrations.verify.capability.publish': 'Publishing',
  'integrations.verify.capability.publish_public': 'Public publishing',
  'integrations.verify.capability.comments': 'Comments',
  'integrations.verify.capability.comments_inbound': 'Comments into the inbox',
  'integrations.verify.capability.org_comments': 'Company Page comments',
  'integrations.verify.capability.mentions': 'Mentions',
  'integrations.verify.capability.ads_read': 'Ads data',
  'integrations.verify.capability.metrics': 'Metrics',
  'integrations.verify.capability.video_metrics': 'Video metrics',
  'integrations.verify.capability.domain_auth': 'Sending domain (SPF/DKIM)',
  'integrations.verify.capability.inbound': 'Incoming email',

  // ── Claude Code / Desktop connect (Studio card, Claude settings) ──────
  'claudeConnect.card.title': 'Use BuzzBit inside Claude Code',
  'claudeConnect.card.subtitle': 'Your Claude, on your machine — no Anthropic key stored here',
  'claudeConnect.card.body':
    'Run these two commands in a terminal. The second opens BuzzBit X in your browser to approve the connection, so there is no key to copy. Your Claude can then read your data and create drafts; sends and activations wait here for your approval.',
  'claudeConnect.copy': 'Copy',
  'claudeConnect.copied': 'Copied',
  'claudeConnect.copyCommands': 'Copy commands',
  'claudeConnect.copyAddress': 'Copy address',
  'claudeConnect.setup.intro':
    'Claude Code and Claude Desktop sign in with your BuzzBit X account — no key. A bz_live_… key from the Keys tab is for the CLI and scripts.',
  'claudeConnect.desktop.pick': 'Settings → Connectors → Add custom connector, then sign in.',
  'claudeConnect.desktop.heading': 'In Claude Desktop:',
  'claudeConnect.desktop.steps':
    'Settings → Connectors → Add custom connector. Paste this address, then sign in with your BuzzBit X account when asked. No key.',

  // ── Outbound webhooks card ───────────────────────────────────────────
  'webhooks.tileTitle': 'Webhooks',
  'webhooks.tileSubtitle': 'Send order, customer, payment and campaign events to your own systems, signed with HMAC.',
  'webhooks.manage': 'Manage',
  'webhooks.connect': 'Add endpoint',
  'webhooks.modalSubtitle': 'Every delivery carries an X-BuzzBit-Signature header (HMAC-SHA256 of the JSON body with your endpoint secret).',
  'webhooks.empty': 'No endpoints yet. Add one to start receiving events.',
  'webhooks.addTitle': 'New endpoint',
  'webhooks.urlLabel': 'Endpoint URL (https)',
  'webhooks.urlPlaceholder': 'https://example.com/webhooks/buzzbit',
  'webhooks.eventsLabel': 'Events',
  'webhooks.save': 'Create endpoint',
  'webhooks.saving': 'Creating…',
  'webhooks.cancel': 'Cancel',
  'webhooks.edit': 'Edit',
  'webhooks.editTitle': 'Edit endpoint',
  'webhooks.update': 'Save changes',
  'webhooks.updating': 'Saving…',
  'webhooks.updated': 'Endpoint updated.',
  'webhooks.secretTitle': 'Signing secret — copy it now',
  'webhooks.secretHint': 'Shown once. Verify X-BuzzBit-Signature with it; you cannot retrieve it later.',
  'webhooks.sendTest': 'Send test',
  'webhooks.testQueued': 'Test event queued — watch the deliveries below.',
  'webhooks.deliveries': 'Deliveries',
  'webhooks.hideDeliveries': 'Hide deliveries',
  'webhooks.noDeliveries': 'No deliveries yet.',
  'webhooks.pause': 'Pause',
  'webhooks.resume': 'Resume',
  'webhooks.delete': 'Delete',
  'webhooks.deleteConfirm': 'Delete this endpoint? Deliveries stop immediately.',
  'webhooks.deleteConfirmTitle': 'Delete webhook endpoint?',
  'webhooks.deleteConfirmBody': 'Deliveries stop immediately. This cannot be undone.',
  'webhooks.deleteConfirmWorking': 'Deleting…',
  'webhooks.paused': 'Paused',
  'webhooks.active': 'Active',
  'webhooks.deadLetterTitle': 'Failed after all retries',
  'webhooks.deadLetterEmpty': 'Nothing in the dead-letter queue.',
  'webhooks.retry': 'Retry',
  'webhooks.retried': 'Moved back to the delivery queue.',
  'webhooks.retryFailed': 'Retry failed. Fix the endpoint and try again.',
  'webhooks.dismissFailed': 'Dismiss',
  'webhooks.dismissed': 'Failed delivery removed.',
  'webhooks.invalidUrl': 'Enter an https:// URL.',
  'webhooks.pickEvent': 'Pick at least one event.',
  'webhooks.failed': 'Something went wrong. Try again.',
  'webhooks.deleted': 'Endpoint deleted.',
  'webhooks.created': 'Endpoint created.',
  'webhooks.attempts': '{count} attempts',
  'webhooks.loading': 'Loading…',
  'webhooks.httpCode': 'HTTP {code}',
  'webhooks.deliveryMeta': '{attempts} attempts · {when}',
  'webhooks.deadLetterRow': '{event} → {url}',
  'webhooks.unknownEvent': 'unknown event',
  // Shown for the server's WHATSAPP_NOT_CONNECTED refusal (services/api/core.ts).
  'integrations.whatsappNotConnected': "WhatsApp isn't connected yet. Connect it in Settings → Integrations → WhatsApp.",
} as const;
